import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Loader2, MessageSquare } from "lucide-react";
import { briefArticle } from "../lib/api";

export default function BriefModal({ article, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!article) return;
    let alive = true;
    setLoading(true); setErr(""); setData(null);
    briefArticle(article.id)
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not generate brief."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [article]);

  if (!article) return null;

  return (
    <div className="modal-back" onClick={onClose} data-testid="brief-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.16em] font-black text-[var(--blue)]">
            <Sparkles size={14} /> Brief Me
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]" aria-label="Close" data-testid="brief-close">
            <X size={20} />
          </button>
        </div>
        <h3 className="text-[20px] font-black leading-snug mb-1">{article.title}</h3>
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="text-[12px] text-[var(--muted)]">{article.source} · {new Date(article.published).toLocaleString()}</span>
          {typeof article.trust === "number" && article.trust > 0 && (
            <span className="text-[10px] uppercase tracking-[0.14em] font-black text-white bg-[var(--blue)] rounded-full px-2 py-0.5" data-testid="brief-trust">
              trust {article.trust}
            </span>
          )}
          {article.lean && (
            <span className={`text-[10px] uppercase tracking-[0.14em] font-black px-1.5 py-0.5 rounded ${
              article.lean === "right" ? "bg-rose-100 text-rose-700" :
              article.lean === "left" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-700"
            }`} data-testid="brief-lean" style={{ display: "none" }}>{article.lean}</span>
          )}
        </div>

        {loading && (
          <div className="py-10 grid place-items-center text-[var(--muted)] text-[13px] font-semibold">
            <Loader2 size={22} className="animate-spin text-[var(--blue)] mb-2" />
            Generating a calm, plain-English brief…
          </div>
        )}
        {err && (
          <div className="p-3 rounded-md bg-[#fff5f5] text-[var(--red)] text-[13px]" data-testid="brief-error">{err}</div>
        )}
        {data && (
          <div data-testid="brief-content">
            <p className="text-[15.5px] leading-[1.6] text-[var(--ink)] mb-4">{data.brief}</p>
            <div className="text-[12px] uppercase tracking-[0.12em] font-bold text-[var(--muted)] mb-2">Key takeaways</div>
            <ul className="space-y-1.5 text-[14px] text-[var(--ink)] list-disc pl-5">
              {data.bullets?.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <div className="mt-4 flex gap-2 items-center flex-wrap">
              <Link to={`/article/${article.id}#ask-us-section`} onClick={onClose} className="btn" data-testid="brief-discuss">
                <MessageSquare size={13} /> Discuss
              </Link>
              <Link to={`/read/${article.id}`} onClick={onClose} className="btn primary" data-testid="brief-read-inframe">Read full story (in-site) →</Link>
              <span className="text-[11px] text-[var(--muted)]">Brief by Newseze Signal Engine{data.cached ? " · cached" : ""}.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
